/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";

import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";

import { useWindowDimensions } from "../../utils/Hooks";
import { InventoryTransfer, Nullable } from "../../utils/Interfaces";
import { lookupTransferType, lookupTransferTypeColor } from "../../utils/Constants";
import DataTable from "../../components/table/DataTable";
import { GridRenderCellParams } from "@mui/x-data-grid";
import Chip from "@mui/material/Chip";
import { OpenInFull } from "@mui/icons-material";
import InventoryTransferDetailsModal from "./InventoryTransferDetailsModal";
import DateIntervalSelector from "../../components/controls/DateIntervalSelector";
import { useMaterialsTransferHistory } from "./materialsTransferHistoryHooks";

const MaterialsTransferHistory = () => {
    const { height } = useWindowDimensions();

    const [selectedInventoryTransfer, setSelectedInventoryTransfer] = useState<Nullable<InventoryTransfer>>(null);
    const [inventoryTransferDetailsModalOpen, setInventoryTransferDetailsModalOpen] = useState<boolean>(false);

    const [startDate, setStartDate] = useState<string>();
    const [endDate, setEndDate] = useState<string>();

    const { data, status } = useMaterialsTransferHistory(startDate ?? "", endDate ?? "");

    let columns: any[] = [
        {
            field: "view",
            width: 60,
            headerName: "View",
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title="View transfer details and attachments">
                        <IconButton
                            onClick={() => {
                                setSelectedInventoryTransfer(params.row);
                                setInventoryTransferDetailsModalOpen(true);
                            }}
                        >
                            <OpenInFull fontSize="small" />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            field: "transfer_type",
            width: 200,
            headerName: "Transfer Type",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return lookupTransferType(params.row.transfer_type);
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Chip
                        color={lookupTransferTypeColor(params.row.transfer_type)}
                        label={lookupTransferType(params.row.transfer_type)}
                        style={{ fontWeight: "bold" }}
                    />
                );
            },
        },
        {
            field: "from_location",
            width: 150,
            headerName: "From Location",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.from_location?.name ?? "";
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography color={params.row.from_location.name === null ? "info.main" : "inherit"}>
                        {params.row.from_location?.name ?? "IN"}
                    </Typography>
                );
            },
        },
        {
            field: "to_location",
            width: 150,
            headerName: "To Location",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.to_location?.name ?? "";
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography
                        color={params.row.to_location.name === null ? "info.main" : "inherit"}
                        variant="subtitle2"
                        noWrap
                        sx={{ textOverflow: "ellipsis", width: 145 }}
                    >
                        {params.row.to_location?.name ?? "OUT"}
                    </Typography>
                );
            },
        },
        {
            field: "material",
            width: 150,
            headerName: "Part/Material",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return `${params.row.material.name} (${params.row.material.part_number})`;
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 145 }}>
                        {`${params.row.material.name}`}
                    </Typography>
                );
            },
        },
        {
            field: "amount",
            width: 80,
            headerName: "Amount",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.amount;
            },
            renderCell: (params: GridRenderCellParams) => {
                return <Typography variant="subtitle2">{params.row.amount}</Typography>;
            },
        },
        {
            field: "date",
            width: 120,
            headerName: "Date",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return new Date(params.row.timestamp).getTime();
            },
            renderCell: (params: GridRenderCellParams) => {
                return <Typography>{new Date(params.row.timestamp).toLocaleDateString()}</Typography>;
            },
        },
        {
            field: "time",
            width: 120,
            headerName: "Time",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return new Date(params.row.timestamp).getTime();
            },
            renderCell: (params: GridRenderCellParams) => {
                return <Typography>{new Date(params.row.timestamp).toLocaleTimeString()}</Typography>;
            },
        },
        {
            field: "user",
            width: 200,
            headerName: "User",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return `${params.row.user.first_name} ${params.row.user.last_name}`;
            },
            renderCell: (params: GridRenderCellParams) => {
                return <Typography>{`${params.row.user.first_name} ${params.row.user.last_name}`}</Typography>;
            },
        },
        {
            field: "notes",
            width: 250,
            headerName: "Notes",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.notes;
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 245 }}>
                        {params.row.notes}
                    </Typography>
                );
            },
        },
    ];

    return (
        <Box>
            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "Materials", route: "/?section=materials" },
                    { text: "Transfer History", route: "/materials/history" },
                ]}
            />

            <InventoryTransferDetailsModal
                transfer={selectedInventoryTransfer}
                open={inventoryTransferDetailsModalOpen}
                handleClose={() => setInventoryTransferDetailsModalOpen(false)}
            />

            <Box sx={{ mt: 3 }} />

            <DateIntervalSelector
                onDatesSelected={(startDate: string, endDate: string) => {
                    setStartDate(startDate);
                    setEndDate(endDate);
                }}
            />

            {status === "pending" && (
                <Stack direction="row" justifyContent="center">
                    <CircularProgress size={40} sx={{ mt: 20 }} />
                </Stack>
            )}

            <Box sx={{ mt: 3 }} />

            {status !== "pending" && (
                <DataTable
                    tableName="MATERIALS_TRANSFER_HISTORY"
                    cellEditStop={() => {}}
                    allowRowSelected={false}
                    height={height - 230}
                    columns={columns}
                    data={data?.results ?? []}
                />
            )}
        </Box>
    );
};

export default MaterialsTransferHistory;
